import { db } from "../lib/firebase";
import { collection, getDocs, limit, query, where } from "../lib/firestoreClient";
import { errorDev } from "../utils/logger";

const RATINGS = "ratings";

/**
 * Canonical rating contract.
 *
 * `firestore.rules` (match /ratings/{ratingId}) only accepts a create when the
 * document carries `raterId`, `targetId` and `targetType`, so those three fields
 * are the canonical representation. `toUserId` / `fromUserId` are legacy
 * aliases that earlier documents (and older writers) used.
 *
 * Firestore cannot OR two fields inside one query, so both recipient fields are
 * read and merged. Writers populate both, so dedupe by document id is enough and
 * a rating written through either field is visible to every reader.
 */
export const RATING_FIELDS = {
  raterId: "raterId",
  raterLegacy: "fromUserId",
  targetId: "targetId",
  targetLegacy: "toUserId",
  targetType: "targetType",
};

export const DEFAULT_RATING_TARGET_TYPE = "user";

export const RECIPIENT_QUERY_FIELDS = [RATING_FIELDS.targetId, RATING_FIELDS.targetLegacy];

const RATING_READ_LIMIT = 200;

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value === "number") return value;
  if (typeof value === "object" && typeof value.seconds === "number") return value.seconds * 1000;
  if (typeof value === "object" && typeof value._seconds === "number") return value._seconds * 1000;
  return new Date(value).getTime() || 0;
}

export function toRatingValue(data = {}) {
  const value = Number(data?.rating ?? data?.stars);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Single interpretation of a stored rating document. Every reader goes through
 * this so no component has to know whether a row uses canonical or legacy
 * field names.
 *
 * @param {{id: string, data: Function}} snapshot Firestore document snapshot.
 * @return {object} normalized rating row.
 */
export function normalizeRating(snapshot) {
  const data = snapshot?.data?.() || {};
  const targetId = data[RATING_FIELDS.targetId] ?? data[RATING_FIELDS.targetLegacy] ?? null;

  return {
    ...data,
    id: snapshot?.id,
    [RATING_FIELDS.raterId]: data[RATING_FIELDS.raterId] ?? data[RATING_FIELDS.raterLegacy] ?? null,
    [RATING_FIELDS.targetId]: targetId === null ? null : String(targetId),
    [RATING_FIELDS.targetType]: data[RATING_FIELDS.targetType] || DEFAULT_RATING_TARGET_TYPE,
    rating: toRatingValue(data),
    createdAtMillis: toMillis(data.createdAt),
  };
}

function dedupeById(rows) {
  const byId = new Map();
  rows.forEach((row) => {
    if (row?.id && !byId.has(row.id)) byId.set(row.id, row);
  });
  return Array.from(byId.values());
}

function sortNewestFirst(rows) {
  return rows.sort((a, b) => (b.createdAtMillis || 0) - (a.createdAtMillis || 0));
}

export const ratingsRepository = {
  path: RATINGS,
  recipientQueryFields: RECIPIENT_QUERY_FIELDS,

  /**
   * Canonical rating document for a writer. `raterId` / `targetId` / `targetType`
   * satisfy the rules; the legacy aliases stay populated so historical documents
   * and older readers keep working.
   *
   * @param {object} input rating input.
   * @return {object} Firestore-ready document body.
   */
  buildDocument({
    raterId,
    targetId,
    targetType = DEFAULT_RATING_TARGET_TYPE,
    rating,
    comment = "",
    extra = {},
  }) {
    if (!raterId) throw new Error("raterId is required");
    if (!targetId) throw new Error("targetId is required");
    if (!String(targetType)) throw new Error("targetType is required");

    const value = Number(rating);
    if (!Number.isInteger(value) || value < 1 || value > 5) {
      throw new Error("rating must be an integer between 1 and 5");
    }

    const rater = String(raterId);
    const target = String(targetId);

    return {
      [RATING_FIELDS.raterId]: rater,
      [RATING_FIELDS.targetId]: target,
      [RATING_FIELDS.targetType]: String(targetType),
      [RATING_FIELDS.raterLegacy]: rater,
      [RATING_FIELDS.targetLegacy]: target,
      rating: value,
      stars: value,
      comment: String(comment || "").trim(),
      ...extra,
    };
  },

  /**
   * All ratings left for a target user, merged across both recipient fields.
   *
   * @param {string} targetId rated user id.
   * @param {object} [options] `{ max, onError }`.
   * @return {Promise<object[]>} normalized rows, newest first.
   */
  async listForTarget(targetId, { max = RATING_READ_LIMIT, onError = null } = {}) {
    if (!targetId) return [];

    const safeMax = Math.max(1, Math.min(Number(max) || RATING_READ_LIMIT, RATING_READ_LIMIT));
    const target = String(targetId);

    const results = await Promise.all(
      RECIPIENT_QUERY_FIELDS.map(async (field) => {
        try {
          return await getDocs(query(collection(db, RATINGS), where(field, "==", target), limit(safeMax)));
        } catch (error) {
          if (onError) onError(error, field);
          else errorDev(`Ratings read failed on "${field}":`, error);
          return null;
        }
      })
    );

    const snapshots = results.filter(Boolean);

    // Every recipient field failing is a real failure, not an empty result.
    if (snapshots.length === 0) {
      const error = new Error("Ratings could not be loaded");
      error.code = "ratings/read-failed";
      throw error;
    }

    const rows = snapshots.flatMap((snap) => (snap?.docs || []).map(normalizeRating));
    return sortNewestFirst(dedupeById(rows));
  },

  /**
   * Average and count for a target user.
   *
   * @param {string} targetId rated user id.
   * @param {object} [options] forwarded to `listForTarget`.
   * @return {Promise<{avg: (number|null), count: number}>}
   */
  async summarizeForTarget(targetId, options = {}) {
    const rows = await this.listForTarget(targetId, options);
    const values = rows.map((row) => row.rating).filter((value) => Number.isFinite(value) && value > 0);

    if (values.length === 0) return { avg: null, count: 0 };

    const total = values.reduce((sum, value) => sum + value, 0);
    return { avg: total / values.length, count: values.length };
  },
};

export default ratingsRepository;